import { Bar } from "react-chartjs-2";
import { Chart as ChartJS } from "chart.js/auto";
import {
  CategoryScale,
  type ChartData,
  type ChartOptions,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  type TooltipModel,
} from "chart.js";
import { formatCurrencyShort } from "~/common/utils/currencyFormatter";
import { ChartTooltip } from "~/features/cms/homepagePersonalization/cpd/components/CPDTooltip";
import ReactDOM from "react-dom/client";
import { useEffect, useRef } from "react";
import {
  commonChartStyleProps,
  createCaret,
  createTooltipElement,
  positionTooltip,
} from "~/features/cms/homepagePersonalization/cpd/CPDUtils";
import { C_CHART_6, C_CHART_7 } from "~/common/components/icon/Colors";
import { styled, ThemeProvider } from "~/common/mui";
import { theme } from "~/app/theme";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

interface LastFiveYearsPerfromanceGraphProps {
  invoice: (number | null)[];
  orderbook: (number | null)[];
  currencyCode: string;
}

export default function LastFiveYearsPerfromanceGraph({
  invoice,
  orderbook,
  currencyCode,
}: LastFiveYearsPerfromanceGraphProps) {
  const tooltipRootRef = useRef<ReactDOM.Root | null>(null);
  const tooltipElRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const tooltipRoot = tooltipRootRef.current;
    const tooltipEl = tooltipElRef.current;

    return () => {
      tooltipRoot?.unmount();
      tooltipEl?.remove();
    };
  }, []);

  const data: ChartData<"bar"> = {
    labels: getYears(),
    datasets: [
      {
        label: "Invoice",
        data: invoice,
        ...commonChartStyleProps(C_CHART_6),
      },
      {
        label: "ConfirmedOrderbook",
        data: orderbook,
        ...commonChartStyleProps(C_CHART_7),
      },
    ],
  };
  const options: ChartOptions<"bar"> = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    scales: {
      x: {
        stacked: true,
        grid: {
          display: false,
        },
      },
      y: {
        stacked: true,
        border: {
          display: false,
        },
        ticks: {
          maxTicksLimit: 6,
          callback: function (value) {
            return formatCurrencyShort(value, currencyCode, "end");
          },
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
        external: (context) =>
          externalTooltipHandler(
            context,
            {
              invoice,
              orderbook,
            },
            tooltipRootRef,
            tooltipElRef,
          ),
      },
    },
  };

  return (
    <ChartView>
      <Bar options={options} data={data} />
    </ChartView>
  );
}

function getYears() {
  const currentYear = new Date().getFullYear();
  const lastFiveYears = [];

  for (let i = 4; i >= 0; i--) {
    lastFiveYears.push(currentYear - i);
  }

  lastFiveYears.push(currentYear + 1);

  return lastFiveYears;
}

const externalTooltipHandler = (
  context: { chart: ChartJS; tooltip: TooltipModel<"bar"> },
  chartData: {
    invoice: (number | null)[];
    orderbook: (number | null)[];
  },
  tooltipRootRef: React.MutableRefObject<ReactDOM.Root | null>,
  tooltipElRef: React.RefObject<HTMLDivElement>,
) => {
  const { invoice = [], orderbook = [] } = chartData;
  const { chart, tooltip } = context;

  const tooltipEl = createTooltipElement(tooltipElRef);

  if (!tooltipRootRef.current) {
    tooltipRootRef.current = ReactDOM.createRoot(tooltipEl);
  }

  if (tooltip.opacity === 0) {
    tooltipEl.style.opacity = "0";
    return;
  }

  const dataPoint = tooltip.dataPoints?.[0];
  if (!dataPoint) {
    return;
  }

  const idx = dataPoint.dataIndex;

  const tooltipData = {
    invoice: invoice[idx] ?? null,
    orderbook: orderbook[idx] ?? null,
    target: null,
    gap: null,
  };

  tooltipRootRef.current.render(
    <ThemeProvider theme={theme}>
      <ChartTooltip data={tooltipData} />
    </ThemeProvider>,
  );

  requestAnimationFrame(() => {
    createCaret(tooltipEl);
    positionTooltip(tooltipEl, chart, dataPoint);
  });
};

const ChartView = styled("div")({
  height: "244px",
});
